/**
 * Secrets Collective — product card behaviour.
 *
 * Swatch selection, colour-aware image swapping and inline quick add, for cards
 * wherever they appear: the product grid, the offer row, anywhere else a card
 * gets rendered.
 *
 * Lived inside sections/sc-product-grid.liquid until the offer row needed the
 * same behaviour. Section JavaScript only ships when that section is on the
 * page, so a card elsewhere had no script at all.
 *
 * Wrap any group of .sc-pcard elements in <sc-cards data-bundle-url="...">.
 * The bundle URL has to be passed in: this file is served from /assets/ but
 * section scripts are served from /compiled_assets/, and a relative import
 * resolves against whichever is doing the importing.
 */

class SCCards extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('.sc-pcard').forEach((card) => this.#bindCard(card));
      this.#bindFilterBar();
    }

    /**
     * Filter and sort the cards already on the page.
     *
     * Everything here is read from data-* attributes that Liquid wrote and
     * compared as plain strings and numbers — nothing is ever inserted as
     * markup, so a product title or type cannot smuggle HTML into the page.
     *
     * Hiding uses the `hidden` attribute rather than display:none so filtered
     * cards leave the accessibility tree too, and sorting reorders real DOM
     * nodes so keyboard and reading order follow what is on screen.
     */
    #bindFilterBar() {
      const filters = Array.from(this.querySelectorAll('[data-filter]'));
      const sorts = Array.from(this.querySelectorAll('[data-sortopt]'));
      if (!filters.length && !sorts.length) return;

      const grid = this.querySelector('.sc-pgrid');
      if (!grid) return;

      const cards = Array.from(grid.querySelectorAll('.sc-pcard'));
      const count = this.querySelector('[data-count]');
      const panels = Array.from(this.querySelectorAll('[data-fs]'));
      // Captured before any sorting so "Featured" can always be restored.
      const original = Array.from(grid.children);

      let active = 'all';
      let mode = 'featured';

      const apply = () => {
        let shown = 0;
        for (const card of cards) {
          // data-type is a tilde-separated list, because a product can carry
          // several tags and match more than one filter.
          const values = (card.dataset.type || '').split('~');
          const match = active === 'all' || values.includes(active);
          card.hidden = !match;
          if (match) shown += 1;
        }
        if (count) count.textContent = `${shown} ${shown === 1 ? 'product' : 'products'} shown`;
      };

      // The summary doubles as the readout, so the current choice is visible
      // without opening the panel.
      const relabel = (details, text) => {
        const label = details?.querySelector('[data-fs-label]');
        if (label) label.textContent = text;
      };

      const close = (details) => details?.removeAttribute('open');

      for (const button of filters) {
        button.addEventListener('click', () => {
          active = button.dataset.filter;
          for (const other of filters) {
            other.setAttribute('aria-pressed', String(other === button));
          }
          const details = button.closest('[data-fs]');
          relabel(details, active === 'all' ? this.dataset.filterLabel || 'Filters' : button.textContent.trim());
          close(details);
          apply();
        });
      }

      for (const button of sorts) {
        button.addEventListener('click', () => {
          mode = button.dataset.sortopt;
          for (const other of sorts) {
            other.setAttribute('aria-pressed', String(other === button));
          }

          if (mode === 'featured') {
            for (const node of original) grid.appendChild(node);
          } else {
            const dir = mode === 'low' ? 1 : -1;
            Array.from(cards)
              .sort((a, b) => (Number(a.dataset.price) - Number(b.dataset.price)) * dir)
              .forEach((card) => grid.appendChild(card));
          }

          const details = button.closest('[data-fs]');
          relabel(details, button.textContent.trim());
          close(details);
          apply();
        });
      }

      // Only one panel open at a time, and clicking away or pressing Escape
      // closes it — what every other dropdown on the web does.
      for (const details of panels) {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          for (const other of panels) if (other !== details) close(other);
        });
      }

      document.addEventListener('click', (event) => {
        for (const details of panels) {
          if (details.open && !details.contains(event.target)) close(details);
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        for (const details of panels) close(details);
      });

      apply();
    }

    #bindCard(card) {
      const swatches = Array.from(card.querySelectorAll('[data-swatch]'));
      const primary = card.querySelector('.sc-pcard__primary');
      const links = card.querySelectorAll('a[href]');

      swatches.forEach((btn) => {
        btn.addEventListener('click', () => {
          swatches.forEach((o) => o.setAttribute('aria-pressed', 'false'));
          btn.setAttribute('aria-pressed', 'true');

          const img = btn.dataset.image;
          if (primary && img && img !== 'null') {
            primary.srcset = '';
            primary.src = img;
          }

          const url = btn.dataset.url;
          if (url) links.forEach((a) => (a.href = url));

          this.#syncSizes(card, swatches);
        });
      });

      this.#bindQuickAdd(card, swatches);
      this.#syncSizes(card, swatches);
    }

    #variants(card) {
      const el = card.querySelector('[data-qa-variants]');
      if (!el) return [];
      try {
        return JSON.parse(el.textContent) || [];
      } catch {
        return [];
      }
    }

    #selectedColour(swatches) {
      const active = swatches.find((s) => s.getAttribute('aria-pressed') === 'true');
      return active ? active.dataset.value : null;
    }

    /** Disable sizes that don't exist in the currently selected colour. */
    #syncSizes(card, swatches) {
      const sizes = card.querySelectorAll('[data-qa-size]');
      if (!sizes.length) return;

      const variants = this.#variants(card);
      const colourPos = Number(card.dataset.colourPos);
      const sizePos = Number(card.dataset.sizePos);
      const colour = this.#selectedColour(swatches);

      sizes.forEach((btn) => {
        const available = variants.some(
          (v) =>
            v.available &&
            v.options[sizePos] === btn.dataset.value &&
            (colourPos < 0 || !colour || v.options[colourPos] === colour)
        );
        btn.disabled = !available;
      });
    }

    #bindQuickAdd(card, swatches) {
      const sizes = card.querySelectorAll('[data-qa-size]');
      if (!sizes.length) return;

      const msg = card.querySelector('[data-qa-msg]');
      const colourPos = Number(card.dataset.colourPos);
      const sizePos = Number(card.dataset.sizePos);

      sizes.forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          if (btn.disabled) return;

          const colour = this.#selectedColour(swatches);
          const variant = this.#variants(card).find(
            (v) =>
              v.available &&
              v.options[sizePos] === btn.dataset.value &&
              (colourPos < 0 || !colour || v.options[colourPos] === colour)
          );
          if (!variant) return;

          btn.classList.add('is-busy');
          if (msg) msg.textContent = '';

          // Loaded on demand so the grid costs nothing until someone adds.
          try {
            const { addItems } = await import(this.dataset.bundleUrl);
            const result = await addItems(card, [{ id: variant.id, quantity: 1 }]);

            btn.classList.remove('is-busy');
            if (msg) msg.textContent = result.ok ? 'Added' : result.message || 'Could not add';
            if (result.ok) setTimeout(() => msg && (msg.textContent = ''), 2000);
          } catch {
            // Never leave the button stuck mid-add with nothing said.
            btn.classList.remove('is-busy');
            if (msg) msg.textContent = 'Could not add';
          }
        });
      });
    }
  }

  if (!customElements.get('sc-cards')) {
    customElements.define('sc-cards', SCCards);
  }
